/**
 * Agent-home quick actions (PlayAd Research / Analyze / Create / Launch template lists — UI copy, not data).
 * Clicking a row fills the composer with `prompt`; the user can edit before sending.
 */
export type TemplateGroupId = "research" | "analyze" | "create" | "launch";

export interface PromptTemplate {
  label: string;
  prompt: string;
}

export interface TemplateGroup {
  id: TemplateGroupId;
  label: string;
  /** Robot-face colour (index.css --chip-* tokens). */
  color: string;
  templates: PromptTemplate[];
}

export const TEMPLATE_GROUPS: TemplateGroup[] = [
  {
    id: "research",
    label: "Research",
    color: "var(--chip-research)",
    templates: [
      { label: "Research competitor ads", prompt: "Research my competitors' ads in the Meta Ad Library. Rank them by days running and tell me which hooks, angles and formats are working." },
      { label: "Create weekly competitor reports", prompt: "Schedule a weekly competitor report: every Monday, sweep my competitors' ad libraries and summarise new long-running ads with 10 ideas mapped to my brand." },
      { label: "Find Market Opportunities", prompt: "Find market opportunities for my brand: gaps competitors aren't covering, under-used angles in reviews, and audiences worth testing." },
      { label: "Seasonal Campaign Ideas", prompt: "Suggest seasonal campaign ideas for the next 60 days, with a hook, angle and format for each, grounded in my brand's reviews and offers." },
    ],
  },
  {
    id: "analyze",
    label: "Analyze",
    color: "var(--chip-analyze)",
    templates: [
      { label: "Analyze your live ads", prompt: "Analyze my live ads: what's working, what's fatiguing, and what should I do next? Use matured data only." },
      { label: "Daily Performance Report", prompt: "Schedule a daily performance report every morning covering spend, CPA, winners, losers and anything that needs my decision." },
      { label: "Weekly Performance Report", prompt: "Create a weekly performance report for last week vs the week before, with campaign and creative findings and prioritised recommendations." },
      { label: "Diagnose Performance Drop", prompt: "My performance dropped recently. Diagnose what changed — spend, CPM, CTR, CVR, creative fatigue, tracking — and tell me the most likely cause." },
      { label: "Check Creative Fatigue", prompt: "Check my creatives for fatigue (frequency climbing, CTR decay, CPA drift) and tell me which ones need a refresh with the same hook." },
      { label: "Review Budget Reallocation", prompt: "Review my budget allocation across campaigns and propose a reallocation that respects the +20%/day scaling rule. Don't change anything yet." },
    ],
  },
  {
    id: "create",
    label: "Create",
    color: "var(--chip-create)",
    templates: [
      { label: "Generate an image ad", prompt: "Generate an image ad for my brand in both 1:1 and 9:16, using my brand assets and a proven hook." },
      { label: "Create an ad clone", prompt: "Clone the structure of a winning reference ad for my brand — keep the composition and hook mechanics, use my own branding and claims." },
      { label: "Create a UGC Video Ad", prompt: "Create a UGC-style video ad script and generate it, with the hook in the first 2 seconds and captions." },
      { label: "Create a recurring creative", prompt: "Set up a recurring creative: every week, generate a fresh hook matrix from my latest winning angles for my approval." },
      { label: "Create Short Video Ads", prompt: "Create a set of short video ads (6–15s) across different emotional triggers, in 9:16." },
    ],
  },
  {
    id: "launch",
    label: "Launch",
    color: "var(--chip-launch)",
    templates: [
      { label: "Launch your first ad", prompt: "Help me launch my first ad: pick my best approved creative, set it up paused with my destination URL and naming convention, and walk me through activation." },
      { label: "Competitor Signal To Campaign Plan", prompt: "Turn my competitors' strongest long-running ads into a campaign plan for my brand: concepts, hooks, formats and a test structure." },
      { label: "Launch Meta Image Campaign", prompt: "Launch a Meta image campaign with my approved creatives in one broad ad set, published paused for my review." },
      { label: "TikTok Video Campaign", prompt: "Set up a TikTok video campaign with my approved video creatives, published paused for my review." },
      { label: "Meta Reels Video Campaign", prompt: "Set up a Meta Reels campaign with my approved 9:16 video creatives, published paused for my review." },
      { label: "UGC Campaign", prompt: "Plan and set up a UGC campaign: generate UGC-style creatives, get my approval, then publish them paused." },
      { label: "Google Keyword Ads", prompt: "Set up Google keyword ads for my brand: propose keywords and ad copy from my site and reviews, published paused for my review." },
    ],
  },
];

export const ROTATING_PLACEHOLDERS = ["Ask anything...", "Create image ads...", "Schedule daily report...", "Analyze competitors...", "Launch my ads..."];

export const ASK_SUGGESTIONS = [
  "What needs my attention on this page?",
  "Summarise what changed vs last week",
  "Explain the biggest movers here",
  "What should I do next?",
];
